import { useEffect, useCallback, useState } from 'react'
import { FirebaseApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  addDoc,
} from 'firebase/firestore/lite'
import styled from 'styled-components'
import { useNavigate } from 'react-location'
import { Spin, Button, message, Modal, PageHeader } from 'antd'
import { FirebaseSessionKey, backgroundImage } from '../constants'
import { isEmpty } from 'lodash'

type Question = {
  _id: string
  question: string
  a1: string
  a2: string
  a3: string
  answer: string
  image: string
  userAnswer: string
}

type Props = {
  firebaseApp: FirebaseApp
  setActiveMenu: (value?: string) => void
  userHasBilling?: boolean
}

const fixedQuestions = [
  '25',
  '10',
  '20',
  '30',
  '40',
  '50',
  '60',
  '70',
  '80',
  '90',
  '100',
  '200',
  '300',
  '85',
  '75',
  '65',
  '55',
  '45',
  '35',
  '1',
]

const NewTestLupa = ({ firebaseApp, setActiveMenu, userHasBilling }: Props) => {
  const [questions, setQuestions] = useState<Question[] | undefined>(undefined)
  const [activeQuestion, setActiveQuestion] = useState<Question | undefined>(
    undefined
  )

  const [finishModalVisible, setFinishModalVisible] = useState<boolean>(false)
  const [hasStarted, setHasStarted] = useState<boolean>(false)

  const navigate = useNavigate()
  const userLogged = window.sessionStorage.getItem(FirebaseSessionKey)

  const questionsCall = useCallback(
    async (ids: string[]) => {
      const db = getFirestore(firebaseApp)
      const citiesCol = collection(db, 'questionslupa')
      const q = query(citiesCol, where('_id', 'in', ids))
      const querySnapshot = await getDocs(q)
      const eventList = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        userAnswer: '',
      }))
      return eventList
    },
    [firebaseApp]
  )

  const generateQuestionIds = () => {
    const numbers = []

    let n
    let p

    const min = 1
    const max = 320
    const r = 20

    for (let i = 0; i < r; i++) {
      do {
        n = Math.floor(Math.random() * (max - min + 1)) + min
        p = numbers.includes(n)
        if (!p) {
          numbers.push(n)
        }
      } while (p)
    }

    return numbers.map(String)
  }

  useEffect(() => {
    if (!userLogged) {
      navigate({ to: '/', replace: true })
    } else {
      if (!questions) {
        const activeIds = userHasBilling
          ? generateQuestionIds()
          : fixedQuestions

        const localQuestions: Question[] = []

        questionsCall(activeIds.slice(0, 10)).then((data1: any) => {
          localQuestions.push(...data1)
          questionsCall(activeIds.slice(10, 20)).then((data2: any) => {
            localQuestions.push(...data2)
            setQuestions(localQuestions as Question[])
            setActiveQuestion(localQuestions[0] as Question)
          })
        })
      }
    }
  }, [navigate, questions, questionsCall, userHasBilling, userLogged])

  if (!userLogged || !questions || !activeQuestion) {
    return (
      <Loading>
        <Spin tip="A Carregar..." size="large" />
      </Loading>
    )
  }

  const handleModalOk = () => {
    setFinishModalVisible(false)
    if (userHasBilling) {
      finishExam()
    } else {
      setActiveMenu(undefined)
      message.info('Teste Cancelado')
      message.error(
        <span>
          <p>Por não ter subscrição ativa, o teste foi cancelado!</p>
          <p>{'Vá até: Minha Conta -> Subscrição para fazer o pagamento'}</p>
        </span>,
        5
      )
    }
  }

  const handleModalCancel = () => {
    setFinishModalVisible(false)
  }

  const changeQuestion = (to: 'up' | 'down') => {
    const currentIndex = questions.findIndex(
      (o) => o._id === activeQuestion._id
    )

    const nextIndex = to === 'up' ? currentIndex + 1 : currentIndex - 1

    if (nextIndex > questions.length - 1) {
      setFinishModalVisible(true)
      return
    }

    if (nextIndex < 0) {
      return
    }

    setActiveQuestion(questions[nextIndex])
  }

  const setAnswer = (prefix: 'a1' | 'a2' | 'a3', fullQuestion: Question) => {
    const holdQuestions = JSON.parse(JSON.stringify(questions))

    const currentIndex = holdQuestions.findIndex(
      (o: Question) => o._id === fullQuestion._id
    )

    const questionWithAnswer = JSON.parse(JSON.stringify(fullQuestion))
    questionWithAnswer.userAnswer = prefix

    holdQuestions.splice(currentIndex, 1, questionWithAnswer)

    setActiveQuestion(questionWithAnswer)
    setQuestions(holdQuestions)
  }

  const renderAnswer = (
    index: string,
    prefix: 'a1' | 'a2' | 'a3',
    fullQuestion: Question
  ) => {
    return (
      <StyledAnswer
        onClick={() => setAnswer(prefix, fullQuestion)}
        isActive={fullQuestion.userAnswer === prefix}
      >
        <p>
          {index} <span>{fullQuestion[prefix]}</span>
        </p>
      </StyledAnswer>
    )
  }

  const renderQuestionCount = () => {
    const currentIndex =
      questions.findIndex((o) => o._id === activeQuestion._id) + 1

    return (
      <QuestionsCount>
        Pergunta {currentIndex} de {questions.length}
      </QuestionsCount>
    )
  }

  const renderImage = (question: Question) => {
    if (isEmpty(question.image)) {
      return null
    } else {
      return (
        <QuestionImageWrapper>
          <img src={question.image} alt="" />
        </QuestionImageWrapper>
      )
    }
  }

  const finishExam = async () => {
    const user = JSON.parse(
      window.sessionStorage.getItem(FirebaseSessionKey) as string
    )

    const wrongAnswers = (questions || []).filter(
      (q: Question) => q.answer !== q.userAnswer
    )

    const percentage = 100 - (wrongAnswers.length * 100) / 20

    const exam = {
      date_finished: new Date(),
      user_id: user.uid,
      wrong_answers: wrongAnswers,
      exam_status: percentage >= 75,
      exam_percentage: percentage,
      exam_id: 'lupa',
    }

    const db = getFirestore(firebaseApp)
    const docRef = await addDoc(collection(db, 'pastexamslupa'), exam)

    if (docRef.id) {
      message.success('Teste Terminado')
      setActiveMenu('examesLUPA:2')
    } else {
      message.error('Não foi possível gravar, tente novamente')
    }
  }

  if (!hasStarted) {
    return (
      <Container>
        <StyledPageHeader
          onBack={() => setActiveMenu(undefined)}
          title="Voltar"
        />
        <ReadyBox>
          <div>
            <h2>Boa sorte futuro Caçador</h2>
            <p>
              Este teste é composto por 20 perguntas, tal como no curso da PSP
            </p>
            <p>
              Para cada pergunta tens 3 respostas, apenas uma está 100% correcta
            </p>
            <p>Podes sempre voltar a tras para rever as perguntas</p>
            <p>
              Quando terminares basta clicar em 'Terminar Teste' e vais ser
              redirecionado para a página de resultados
            </p>
          </div>
          <Button
            type="primary"
            onClick={() => {
              setHasStarted(true)
            }}
          >
            Começar
          </Button>
        </ReadyBox>
      </Container>
    )
  }

  return (
    <Container>
      <Modal
        title="Terminar Teste"
        visible={finishModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        cancelText={'Cancelar'}
        okText={'Terminar'}
        closable={false}
        centered
      >
        <p>Tem a certeza que pretende terminar o teste?</p>
      </Modal>
      <Navigation>
        <NavigationActionContainer>
          <Button
            type="default"
            onClick={() => {
              message.info('Teste Cancelado')
              setActiveMenu(undefined)
            }}
            style={{ margin: '20px' }}
          >
            Cancelar Teste
          </Button>
          <Button
            type="default"
            onClick={() => {
              setFinishModalVisible(true)
            }}
            style={{ margin: '20px' }}
          >
            Terminar Teste
          </Button>
        </NavigationActionContainer>
      </Navigation>
      {!userHasBilling && (
        <StyledWarning>
          {
            '* Isto é apenas um teste exemplo, para ter acesso a mais testes aceda a Minha Conta -> Subscrição *'
          }
        </StyledWarning>
      )}
      <QuestionContainer>
        {renderQuestionCount()}
        <StyledQuestion>
          <p>
            Pergunta - <span>{activeQuestion.question}</span>
          </p>
        </StyledQuestion>
        {renderImage(activeQuestion)}
        {renderAnswer('A): ', 'a1', activeQuestion)}
        {renderAnswer('B): ', 'a2', activeQuestion)}
        {renderAnswer('C): ', 'a3', activeQuestion)}
        <QuestionActionContainer>
          <Button type="primary" onClick={() => changeQuestion('down')}>
            Anterior
          </Button>
          <Button type="primary" onClick={() => changeQuestion('up')}>
            Próxima
          </Button>
        </QuestionActionContainer>
      </QuestionContainer>
    </Container>
  )
}

export default NewTestLupa

const ReadyBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #fafafa;
  border: 1px solid #fafafa;
  border-radius: 5px;
  width: 50%;
  height: 50%;
  padding: 10px;
  color: black;

  @media (max-width: 768px) {
    width: auto;
    height: auto;
    max-width: 90%;
    max-height: 90%;
    overflow-y: auto;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(100% - 100px);
  max-height: calc(100% - 100px);
  background: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)),
    url(${backgroundImage});
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;

  @media (max-width: 768px) {
    justify-content: flex-start;
  }
`
const Navigation = styled.div`
  width: 100%;
  height: 40px;
  margin-bottom: 20px;
`
const Loading = styled.div`
  display: flex;
  width: 100%;
  height: calc(100% - 100px);
  align-items: center;
  justify-content: center;
`

const QuestionImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
`

const StyledQuestion = styled.div`
  background-color: #e8e8e8;
  font-weight: bold;
  padding: 15px;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  margin-bottom: 30px;
  color: black;

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 5px;
  }
`

const StyledAnswer = styled.div<{ isActive: boolean }>`
  background-color: ${({ isActive }) => (isActive ? '#00A7F0' : '#FAFAFA')};
  padding: 15px;
  border: 2px solid #fafafa;
  border-radius: 5px;
  margin-bottom: 10px;
  color: black;

  &:hover {
    background-color: ${({ isActive }) => (isActive ? '#00A7F0' : 'lightgray')};
    cursor: pointer;
  }

  p {
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 5px;
  }
`

const QuestionContainer = styled.div`
  width: 800px;
  max-width: 800px;

  @media (max-width: 768px) {
    width: 95%;
    max-width: 95%;
  }
`
const NavigationActionContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: auto;

  @media (max-width: 768px) {
    min-width: 95%;
    max-width: 95%;
  }
`

const StyledWarning = styled.p`
  color: orange;
  font-weight: bold;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`

const QuestionActionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 10px;
`
const QuestionsCount = styled.p`
  color: #fafafa;
  font-weight: bold;
`
const StyledPageHeader = styled(PageHeader)`
  span {
    color: #fafafa;
  }
`
